import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { useCallback, useEffect, useState } from "react";
import { AppUpdateState, checkForAppUpdate } from "../native/appUpdate";
import { isNativeApp } from "../native/platform";

/** Remembers the version the user said "later" to, so we stop nagging. */
const DISMISSED_KEY = "app_update_dismissed_version";

const readDismissed = (): string | null => {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    // Private mode / storage disabled — the modal just shows again next launch.
    return null;
  }
};

const writeDismissed = (version: string): void => {
  try {
    localStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // ignore
  }
};

export const useAppUpdate = () => {
  const [update, setUpdate] = useState<AppUpdateState | null>(null);

  const runCheck = useCallback(async () => {
    const result = await checkForAppUpdate();

    // "Later" holds until a newer version ships. A required update ignores it.
    if (
      result?.requirement === "optional" &&
      readDismissed() === result.latestVersion
    ) {
      setUpdate(null);
      return;
    }

    setUpdate(result);
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;

    runCheck();

    // Re-check when the user comes back to the app — most importantly after
    // being sent to the App Store, so the modal clears itself on return.
    let handle: PluginListenerHandle | undefined;
    let cancelled = false;
    App.addListener("resume", runCheck).then((listener) => {
      if (cancelled) {
        listener.remove();
        return;
      }
      handle = listener;
    });

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [runCheck]);

  const dismiss = useCallback(() => {
    if (update?.requirement !== "optional") return;
    writeDismissed(update.latestVersion);
    setUpdate(null);
  }, [update]);

  return { update, dismiss };
};
